import { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Modal, Form, Input, InputNumber, Select, message, Statistic, Row, Col, Tabs, Progress, Alert, Tooltip, Popconfirm, Typography, Empty, Divider } from 'antd';
import { SafetyCertificateOutlined, ReloadOutlined, DeleteOutlined, ClockCircleOutlined, ThunderboltOutlined, UserOutlined, KeyOutlined, GlobalOutlined, ApiOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons';
import { adminAPI, internalGatewayAPI } from '../services/api';
import { t } from '../utils/i18n';
import dayjs from 'dayjs';

const { Text } = Typography;
const { TextArea } = Input;

const RateLimits = () => {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [overview, setOverview] = useState(null);
  const [topUsers, setTopUsers] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [userStats, setUserStats] = useState([]);
  const [keyStats, setKeyStats] = useState([]);
  const [ipStats, setIpStats] = useState([]);
  const [modelStats, setModelStats] = useState([]);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [userLimitModalVisible, setUserLimitModalVisible] = useState(false);
  const [modelLimitModalVisible, setModelLimitModalVisible] = useState(false);
  const [editingModel, setEditingModel] = useState(null); // 正在编辑的模型名称（null 表示新增）
  const [modelLimitForm] = Form.useForm();
  const [form] = Form.useForm();
  const [userLimitForm] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, []);

  // 模拟数据
  const mockData = {
    config: {
      user_limit: { limit: 60, window: 60 },
      key_limit: { limit: 100, window: 60 },
      ip_limit: { limit: 120, window: 60 },
      model_limit: { 'qwen-turbo': { limit: 200, window: 60 } },
    },
    overview: {
      active_users: 156,
      active_keys: 423,
      active_ips: 89,
    },
    user_stats: {
      'user_001': { reqs_window: 45, limit: 60, usage_rate: 75 },
      'user_002': { reqs_window: 12, limit: 60, usage_rate: 20 },
      'user_003': { reqs_window: 58, limit: 60, usage_rate: 97 },
      'user_004': { reqs_window: 8, limit: 60, usage_rate: 13 },
      'user_005': { reqs_window: 23, limit: 60, usage_rate: 38 },
    },
    key_stats: {
      'sk-test-001': { reqs_window: 78, limit: 100, usage_rate: 78 },
      'sk-test-002': { reqs_window: 34, limit: 100, usage_rate: 34 },
      'sk-test-003': { reqs_window: 95, limit: 100, usage_rate: 95 },
    },
    ip_stats: {
      '192.168.1.100': { reqs_window: 67, limit: 120, usage_rate: 56 },
      '192.168.1.101': { reqs_window: 23, limit: 120, usage_rate: 19 },
      '192.168.1.102': { reqs_window: 112, limit: 120, usage_rate: 93 },
    },
    model_stats: {
      'qwen-turbo': { reqs_window: 234, limit: 200, usage_rate: 100 },
      'qwen-plus': { reqs_window: 156, limit: 200, usage_rate: 78 },
    },
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // 直接调用 internal-gateway API
      const [configRes, overviewRes, topUsersRes, blockedRes, usageRes] = await Promise.all([
        internalGatewayAPI.get('/admin/ratelimit'),
        internalGatewayAPI.get('/admin/usage/overview'),
        internalGatewayAPI.get('/admin/usage/top-users'),
        internalGatewayAPI.get('/admin/usage/blocked'),
        internalGatewayAPI.get('/admin/usage'),
      ]);

      // 使用API数据 - internalGatewayAPI拦截器返回response.data，所以configRes是响应体 {code, data}
      // 需要访问 .data 获取实际配置
      if (configRes?.data) {
        setConfig(configRes.data);
      } else if (configRes && !configRes.data) {
        // 兼容：如果configRes没有data字段，说明拦截器行为有变，直接使用
        setConfig(configRes);
      }
      if (overviewRes) {
        setOverview(overviewRes);
      }
      if (topUsersRes?.users) {
        setTopUsers(topUsersRes.users);
      }
      if (blockedRes?.blocked_users) {
        setBlockedUsers(blockedRes.blocked_users);
      }
      
      // 处理用量统计数据 - usageRes 可能是 {user_stats, ...} 或 {code, data: {user_stats, ...}}
      // 统一处理两种格式
      const usageData = usageRes?.data || usageRes || {};
      
      // 从 Redis 实时读取最新配置作为限额值
      const latestConfig = configRes?.data || configRes || {};
      const defaultUserLimit = latestConfig?.user_limit?.limit || latestConfig?.user_limit?.requests_per_window || 60;
      const defaultKeyLimit = latestConfig?.key_limit?.limit || latestConfig?.key_limit?.requests_per_window || 100;
      const defaultIpLimit = latestConfig?.ip_limit?.limit || latestConfig?.ip_limit?.requests_per_window || 120;
      
      if (usageData) {
        if (usageData.user_stats) {
          setUserStats(Object.entries(usageData.user_stats).map(([user_id, stat]) => {
            const reqs = stat.requests_in_window || stat.reqs_window || stat.req_count || 0;
            const limit = stat.custom_limit || defaultUserLimit;
            return { 
              user_id, 
              reqs_window: reqs,
              limit: limit,
              usage_rate: limit > 0 ? Math.round((reqs / limit) * 100) : 0,
            };
          }));
        } else {
          setUserStats([]);
        }
        if (usageData.key_stats) {
          setKeyStats(Object.entries(usageData.key_stats).map(([key, stat]) => {
            const reqs = stat.requests_in_window || stat.reqs_window || stat.req_count || 0;
            const limit = stat.custom_limit || defaultKeyLimit;
            return { 
              key, 
              reqs_window: reqs,
              limit: limit,
              usage_rate: limit > 0 ? Math.round((reqs / limit) * 100) : 0,
            };
          }));
        } else {
          setKeyStats([]);
        }
        if (usageData.ip_stats) {
          setIpStats(Object.entries(usageData.ip_stats).map(([ip, stat]) => {
            const reqs = stat.requests_in_window || stat.reqs_window || stat.req_count || 0;
            const limit = stat.custom_limit || defaultIpLimit;
            return { 
              ip, 
              reqs_window: reqs,
              limit: limit,
              usage_rate: limit > 0 ? Math.round((reqs / limit) * 100) : 0,
            };
          }));
        } else {
          setIpStats([]);
        }
        if (usageData.model_stats) {
          setModelStats(Object.entries(usageData.model_stats).map(([key, stat]) => ({ 
            ...stat,
            reqs_window: stat.requests_in_window || stat.reqs_window || stat.req_count,
          })));
        } else {
          setModelStats([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      message.error('获取用量数据失败，请检查 internal-gateway 服务是否运行');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateConfig = async (values) => {
    try {
      await internalGatewayAPI.put('/admin/ratelimit', values);
      message.success(t('success'));
      setConfigModalVisible(false);
      fetchData();
    } catch (error) {
      message.error(t('error') + ': ' + (error.message || 'Failed to update config'));
    }
  };

  const handleResetLimit = async (type, id, model = null) => {
    try {
      let url = `/admin/ratelimit/reset?type=${type}&id=${id}`;
      if (model) url += `&model=${model}`;
      await internalGatewayAPI.post(url);
      message.success(t('success'));
      fetchData();
    } catch (error) {
      message.error(t('error') + ': ' + (error.message || 'Failed to reset'));
    }
  };

  const handleApplyUserLimit = async (values) => {
    try {
      await internalGatewayAPI.post('/admin/usage/user-limit', values);
      message.success(t('success'));
      setUserLimitModalVisible(false);
      userLimitForm.resetFields();
    } catch (error) {
      message.error(t('error') + ': ' + (error.message || 'Failed to apply limit'));
    }
  };

  const handleAddModelLimit = async (values) => {
    try {
      // 构建 model_limit 对象
      const modelLimit = {};
      modelLimit[values.model] = {
        limit: values.limit,
        window: values.window,
      };
      await internalGatewayAPI.put('/admin/ratelimit', { model_limit: modelLimit });
      message.success(editingModel ? t('model_edited_success') : t('success'));
      setModelLimitModalVisible(false);
      setEditingModel(null);
      modelLimitForm.resetFields();
      fetchData();
    } catch (error) {
      message.error(t('error') + ': ' + (error.message || 'Failed to add model limit'));
    }
  };

  const handleEditModelLimit = (model, limit, window) => {
    setEditingModel(model);
    modelLimitForm.setFieldsValue({
      model: model,
      limit: limit,
      window: window,
    });
    setModelLimitModalVisible(true);
  };

  const handleOpenAddModelLimit = () => {
    setEditingModel(null);
    modelLimitForm.resetFields();
    setModelLimitModalVisible(true);
  };

  const handleRemoveModelLimit = async (model) => {
    try {
      // 将 limit 设为 0 表示移除该模型的限流配置
      const modelLimit = {};
      modelLimit[model] = {
        limit: 0,
        window: 0,
      };
      await internalGatewayAPI.put('/admin/ratelimit', { model_limit: modelLimit });
      message.success(t('success'));
      fetchData();
    } catch (error) {
      message.error(t('error') + ': ' + (error.message || 'Failed to remove model limit'));
    }
  };

  const userColumns = [
    {
      title: t('user_id'),
      dataIndex: 'user_id',
      key: 'user_id',
      width: 150,
      render: (text) => <Text style={{ fontFamily: 'monospace' }}>{text}</Text>,
    },
    {
      title: t('requests_in_window'),
      dataIndex: 'reqs_window',
      key: 'reqs_window',
      width: 100,
      render: (val) => <span style={{ color: val > 50 ? '#ff4d4f' : '#52c41a' }}>{val}</span>,
    },
    {
      title: t('limit'),
      dataIndex: 'limit',
      key: 'limit',
      width: 80,
    },
    {
      title: t('usage_rate'),
      dataIndex: 'usage_rate',
      key: 'usage_rate',
      width: 150,
      render: (val) => <Progress percent={Math.min(val, 100)} size="small" status={val > 80 ? 'exception' : 'active'} />,
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => {
            userLimitForm.setFieldsValue({ user_id: record.user_id });
            setUserLimitModalVisible(true);
          }}>
            {t('edit')}
          </Button>
          <Popconfirm title={t('confirm_reset')} onConfirm={() => handleResetLimit('user', record.user_id)}>
            <Button size="small" danger icon={<ReloadOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const keyColumns = [
    {
      title: 'API Key',
      dataIndex: 'key',
      key: 'key',
      width: 200,
      render: (text) => <Text copyable={{ text: text }} style={{ fontFamily: 'monospace', fontSize: 11 }} ellipsis={{ showTitle: false }}>{text}</Text>,
    },
    {
      title: t('requests_in_window'),
      dataIndex: 'reqs_window',
      key: 'reqs_window',
      width: 100,
    },
    {
      title: t('limit'),
      dataIndex: 'limit',
      key: 'limit',
      width: 80,
      render: (val) => val || '-',
    },
    {
      title: t('usage_rate'),
      dataIndex: 'usage_rate',
      key: 'usage_rate',
      width: 150,
      render: (val) => <Progress percent={Math.min(val, 100)} size="small" />,
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Popconfirm title={t('confirm_reset')} onConfirm={() => handleResetLimit('key', record.key)}>
          <Button size="small" danger icon={<ReloadOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const ipColumns = [
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      width: 150,
      render: (text) => <Text style={{ fontFamily: 'monospace' }}>{text}</Text>,
    },
    {
      title: t('requests_in_window'),
      dataIndex: 'reqs_window',
      key: 'reqs_window',
      width: 100,
    },
    {
      title: t('limit'),
      dataIndex: 'limit',
      key: 'limit',
      width: 80,
      render: (val) => val || '-',
    },
    {
      title: t('usage_rate'),
      dataIndex: 'usage_rate',
      key: 'usage_rate',
      width: 150,
      render: (val) => <Progress percent={Math.min(val, 100)} size="small" />,
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Popconfirm title={t('confirm_reset')} onConfirm={() => handleResetLimit('ip', record.ip)}>
          <Button size="small" danger icon={<ReloadOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const modelColumns = [
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      width: 180,
      render: (text, record) => {
        // user_id 格式为 "user:126" 或 "ip:192.168.1.1"
        const idParts = record.user_id.split(':');
        const idType = idParts[0];
        const idValue = idParts.slice(1).join(':');
        return (
          <Space direction="vertical" size={0}>
            <Tag color="blue">{text}</Tag>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {idType === 'user' ? '用户' : 'IP'}: {idValue}
            </Text>
          </Space>
        );
      },
    },
    {
      title: t('requests_in_window'),
      dataIndex: 'reqs_window',
      key: 'reqs_window',
      width: 100,
    },
    {
      title: t('limit'),
      dataIndex: 'limit',
      key: 'limit',
      width: 80,
    },
    {
      title: t('usage_rate'),
      dataIndex: 'usage_rate',
      key: 'usage_rate',
      width: 150,
      render: (val) => <Progress percent={Math.min(val, 100)} size="small" />,
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Popconfirm title={t('confirm_reset')} onConfirm={() => handleResetLimit('model', record.user_id, record.model)}>
          <Button size="small" danger icon={<ReloadOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  // 模型限流配置列表（从配置中获取已配置的模型限流）
  const modelLimitConfigs = config?.model_limit ? Object.entries(config.model_limit).map(([model, cfg]) => ({
    model,
    limit: cfg.limit,
    window: cfg.window,
  })) : [];

  const tabItems = [
    {
      key: 'users',
      label: <span><UserOutlined /> {t('user_list')}</span>,
      children: (
        <Table
          columns={userColumns}
          dataSource={userStats}
          rowKey="user_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      ),
    },
    {
      key: 'keys',
      label: <span><KeyOutlined /> API Keys</span>,
      children: (
        <Table
          columns={keyColumns}
          dataSource={keyStats}
          rowKey="key"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      ),
    },
    {
      key: 'ips',
      label: <span><GlobalOutlined /> IPs</span>,
      children: (
        <Table
          columns={ipColumns}
          dataSource={ipStats}
          rowKey="ip"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      ),
    },
    {
      key: 'models',
      label: <span><ApiOutlined /> {t('model_list')}</span>,
      children: (
        <>
          {/* 已配置的模型限流列表 */}
          {modelLimitConfigs.length > 0 ? (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={[8, 8]} align="middle">
                <Col>
                  <Text strong>{t('configured_model_limits')}: </Text>
                </Col>
                {modelLimitConfigs.map(({ model, limit, window }) => (
                  <Col key={model}>
                    <Space>
                      <Tag 
                        color="orange" 
                        closable 
                        onClose={() => handleRemoveModelLimit(model)}
                        style={{ fontSize: 12, padding: '4px 8px' }}
                      >
                        {model}: {limit}次/{window}s
                      </Tag>
                      <Tooltip title={t('edit')}>
                        <Button 
                          type="text" 
                          size="small" 
                          icon={<EditOutlined />}
                          onClick={() => handleEditModelLimit(model, limit, window)}
                        />
                      </Tooltip>
                    </Space>
                  </Col>
                ))}
                <Col>
                  <Button 
                    type="dashed" 
                    size="small" 
                    icon={<PlusOutlined />}
                    onClick={handleOpenAddModelLimit}
                  >
                    {t('add')}
                  </Button>
                </Col>
              </Row>
            </Card>
          ) : (
            <Alert
              message={t('no_model_limits_configured')}
              description={<Button type="primary" icon={<PlusOutlined />} onClick={() => setModelLimitModalVisible(true)}>{t('add_model_limit')}</Button>}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <Table
            columns={modelColumns}
            dataSource={modelStats}
            rowKey={(record) => `${record.model}:${record.user_id}`}
            loading={loading}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: <Empty description={t('no_model_usage_data')} /> }}
          />
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between' }}>
        <h2><SafetyCertificateOutlined /> {t('rate_limit_management')}</h2>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchData}>{t('refresh')}</Button>
          <Button type="primary" icon={<SafetyCertificateOutlined />} onClick={() => setConfigModalVisible(true)}>
            {t('config')}
          </Button>
        </Space>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('active_users')}
              value={overview?.active_users || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('active_keys')}
              value={overview?.active_keys || 0}
              prefix={<KeyOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('active_ips')}
              value={overview?.active_ips || 0}
              prefix={<GlobalOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('blocked_count')}
              value={blockedUsers.length}
              prefix={<ThunderboltOutlined />}
              valueStyle={{ color: blockedUsers.length > 0 ? '#cf1322' : '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 当前限流配置 */}
      {config && (
        <Card title={t('current_config')} style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Tag color="blue" icon={<UserOutlined />} style={{ fontSize: 14, padding: '4px 8px' }}>
                {t('user_limit')}: {config.user_limit?.limit || 60}次/{config.user_limit?.window || 60}s
              </Tag>
            </Col>
            <Col span={6}>
              <Tag color="green" icon={<KeyOutlined />} style={{ fontSize: 14, padding: '4px 8px' }}>
                {t('key_limit')}: {config.key_limit?.limit || 100}次/{config.key_limit?.window || 60}s
              </Tag>
            </Col>
            <Col span={6}>
              <Tag color="purple" icon={<GlobalOutlined />} style={{ fontSize: 14, padding: '4px 8px' }}>
                {t('ip_limit')}: {config.ip_limit?.limit || 120}次/{config.ip_limit?.window || 60}s
              </Tag>
            </Col>
            <Col span={6}>
              <Tag color="orange" icon={<ApiOutlined />} style={{ fontSize: 14, padding: '4px 8px' }}>
                {t('model_limit')}: {Object.keys(config.model_limit || {}).length} {t('models_configured')}
              </Tag>
            </Col>
          </Row>
        </Card>
      )}

      {/* 被限流用户告警 */}
      {blockedUsers.length > 0 && (
        <Alert
          message={`${blockedUsers.length} ${t('users_blocked')}`}
          description={
            <div>
              {blockedUsers.slice(0, 5).map((user) => (
                <Tag key={user.user_id || user.id} color="red">{user.user_id}: {user.req_count} {t('requests')}</Tag>
              ))}
              {blockedUsers.length > 5 && <span>... +{blockedUsers.length - 5} {t('more')}</span>}
            </div>
          }
          type="warning"
          showIcon
          icon={<ThunderboltOutlined />}
          style={{ marginBottom: 24 }}
        />
      )}

      {/* 用量统计表格 */}
      <Card>
        <Tabs items={tabItems} />
      </Card>

      {/* 限流配置弹窗 */}
      <Modal
        title={t('rate_limit_config')}
        open={configModalVisible}
        onCancel={() => setConfigModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={config}
          onFinish={handleUpdateConfig}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t('user_limit')} name={['user_limit', 'limit']}>
                <InputNumber min={1} max={1000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('window_seconds')} name={['user_limit', 'window']}>
                <InputNumber min={1} max={3600} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t('key_limit')} name={['key_limit', 'limit']}>
                <InputNumber min={1} max={1000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('window_seconds')} name={['key_limit', 'window']}>
                <InputNumber min={1} max={3600} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t('ip_limit')} name={['ip_limit', 'limit']}>
                <InputNumber min={1} max={1000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('window_seconds')} name={['ip_limit', 'window']}>
                <InputNumber min={1} max={3600} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">{t('save')}</Button>
              <Button onClick={() => setConfigModalVisible(false)}>{t('cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 用户自定义限流弹窗 */}
      <Modal
        title={t('set_user_limit')}
        open={userLimitModalVisible}
        onCancel={() => setUserLimitModalVisible(false)}
        footer={null}
      >
        <Form
          form={userLimitForm}
          layout="vertical"
          onFinish={handleApplyUserLimit}
        >
          <Form.Item
            label={t('user_id')}
            name="user_id"
            rules={[{ required: true, message: t('required') }]}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item
            label={t('limit')}
            name="limit"
            rules={[{ required: true, message: t('required') }]}
          >
            <InputNumber min={1} max={1000} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            label={t('window_seconds')}
            name="window_seconds"
            rules={[{ required: true, message: t('required') }]}
          >
            <InputNumber min={1} max={3600} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">{t('save')}</Button>
              <Button onClick={() => setUserLimitModalVisible(false)}>{t('cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 模型限流配置弹窗 */}
      <Modal
        title={editingModel ? t('edit_model_limit') : t('add_model_limit')}
        open={modelLimitModalVisible}
        onCancel={() => { setModelLimitModalVisible(false); setEditingModel(null); }}
        footer={null}
      >
        <Form
          form={modelLimitForm}
          layout="vertical"
          onFinish={handleAddModelLimit}
        >
          <Form.Item
            label={t('model_name')}
            name="model"
            rules={[{ required: true, message: t('required') }]}
            extra={editingModel ? null : t('model_name_hint')}
          >
            <Input placeholder="e.g. deepseek-v4-flash, qwen-turbo, gpt-4o" disabled={!!editingModel} />
          </Form.Item>
          <Form.Item
            label={t('limit')}
            name="limit"
            rules={[{ required: true, message: t('required') }]}
            extra={t('limit_per_window')}
          >
            <InputNumber min={1} max={10000} style={{ width: '100%' }} placeholder="e.g. 50" />
          </Form.Item>
          <Form.Item
            label={t('window_seconds')}
            name="window"
            rules={[{ required: true, message: t('required') }]}
            extra={t('window_seconds_hint')}
          >
            <InputNumber min={1} max={3600} style={{ width: '100%' }} placeholder="e.g. 60" />
          </Form.Item>
          <Divider />
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">{t('save')}</Button>
              <Button onClick={() => { setModelLimitModalVisible(false); setEditingModel(null); }}>{t('cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RateLimits;
